import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Container, IconButton, Stack, Typography } from '@mui/material';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ProductsStore } from '../../utils/stores/ProductsStore';
import HeroFeaturedProductCard from './HeroFeaturedProductCard';

interface HeroSectionProps {
    readonly productCodes: string[];
}

const HERO_BACKDROP_URL =
    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/products/reef-hopper/green-island-scenic-flight-cairns.webp';

const navBtnSx = {
    color: 'grey.900',
    bgcolor: 'rgba(255,255,255,0.55)',
    border: '1px solid rgba(255,255,255,0.85)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.75)' },
    '&.Mui-disabled': {
        color: 'rgba(0,0,0,0.28)',
        bgcolor: 'rgba(255,255,255,0.25)',
        borderColor: 'rgba(255,255,255,0.35)',
    },
} as const;

const heroFont = {
    fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
} as const;

const CARD_SCROLL_GAP_PX = 16;

export default function HeroSection({ productCodes }: HeroSectionProps) {
    const productController = useContext(ProductsStore);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const isOnSale = useCallback(
        (code: string) => {
            const product = productController.products.filter((prod) => prod.productCode === code)[0];
            const sale = product?.sales?.filter((s) => moment().isAfter(s.startDate) && moment().isBefore(s.endDate));
            const salePrice = sale && sale.length !== 0 ? sale[0].fareTypes[0].price : null;
            return salePrice;
        },
        [productController.products]
    );

    const updateScrollHints = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanScrollPrev(scrollLeft > 2);
        setCanScrollNext(scrollLeft < scrollWidth - clientWidth - 2);

        // Fade any card that isn't fully in view so the peeked next card reads as secondary.
        const containerRect = el.getBoundingClientRect();
        Array.from(el.children).forEach((child) => {
            const card = child as HTMLElement;
            const rect = card.getBoundingClientRect();
            const fullyVisible = rect.left >= containerRect.left - 1 && rect.right <= containerRect.right + 1;
            card.style.opacity = fullyVisible ? '1' : '0.2';
        });
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateScrollHints, { passive: true });
        updateScrollHints();
        const ro = new ResizeObserver(() => updateScrollHints());
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScrollHints);
            ro.disconnect();
        };
    }, [updateScrollHints, productCodes.length, productController.isLoading]);

    const scrollByOne = useCallback((direction: -1 | 1) => {
        const el = scrollRef.current;
        if (!el) return;
        const card = el.querySelector('[data-hero-card]') as HTMLElement | null;
        const step = (card?.offsetWidth ?? 400) + CARD_SCROLL_GAP_PX;
        el.scrollBy({ left: direction * step, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (productCodes.length <= 1) return;
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            const t = e.target as HTMLElement | null;
            if (t?.closest('input, textarea, select, [contenteditable="true"]')) return;
            const section = scrollRef.current?.closest('section');
            if (!section?.contains(document.activeElement)) return;
            e.preventDefault();
            scrollByOne(e.key === 'ArrowLeft' ? -1 : 1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [productCodes.length, scrollByOne]);

    if (productCodes.length === 0) {
        return null;
    }

    const showNav = productCodes.length > 1;

    return (
        <Box
            component="section"
            aria-label="Featured tours"
            tabIndex={0}
            sx={{
                display: 'grid',
                gridTemplateColumns: '100%',
                width: '100%',
                overflow: 'hidden',
                '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.light',
                    outlineOffset: 2,
                },
            }}
        >
            <Box
                aria-hidden
                sx={{
                    gridColumn: 1,
                    gridRow: 1,
                    zIndex: 0,
                    minHeight: '100%',
                    backgroundImage: `url(${HERO_BACKDROP_URL})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(1.5px) brightness(0.86)',
                    transform: 'scale(1.03)',
                }}
            />
            {/* Even dark wash so headline stays readable; cards stay the bright focal point */}
            <Box
                aria-hidden
                sx={{
                    gridColumn: 1,
                    gridRow: 1,
                    zIndex: 0,
                    minHeight: '100%',
                    bgcolor: 'rgba(15, 20, 28, 0.38)',
                }}
            />
            <Box
                aria-hidden
                sx={{
                    gridColumn: 1,
                    gridRow: 1,
                    zIndex: 0,
                    minHeight: '100%',
                    background:
                        'linear-gradient(180deg, rgba(8, 12, 18, 0.15) 0%, rgba(8, 12, 18, 0.22) 38%, rgba(6, 10, 16, 0.42) 72%, rgba(5, 8, 14, 0.5) 100%)',
                }}
            />

            <Container
                maxWidth="lg"
                sx={{
                    gridColumn: 1,
                    gridRow: 1,
                    position: 'relative',
                    zIndex: 1,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 4, sm: 5, md: 6 },
                }}
            >
                <Stack spacing={3} sx={{ mb: 3, ...heroFont }}>
                    <Typography
                        variant="overline"
                        sx={{
                            color: 'primary.main',
                            letterSpacing: '0.18em',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            m: 0,
                        }}
                    >
                        Cairns, Australia
                    </Typography>
                    <Box>
                        <Typography
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '2.85rem' },
                                lineHeight: 1.12,
                                color: 'common.white',
                                m: 0,
                                mb: 0.5,
                                textShadow: '0 1px 18px rgba(0,0,0,0.25)',
                            }}
                        >
                            Great Barrier Reef
                        </Typography>
                        <Typography
                            component="p"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', sm: '2.2rem', md: '2.45rem' },
                                lineHeight: 1.12,
                                color: 'primary.main',
                                m: 0,
                                textShadow: '0 1px 14px rgba(0,0,0,0.2)',
                            }}
                        >
                            Scenic Flights
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.95)',
                            fontSize: { xs: '0.95rem', sm: '1.05rem' },
                            fontWeight: 500,
                            maxWidth: 560,
                            m: 0,
                            lineHeight: 1.55,
                            textShadow: '0 1px 10px rgba(0,0,0,0.2)',
                        }}
                    >
                        Choose your adventure — swipe to explore our experiences.
                    </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
                        {showNav && (
                            <>
                                <IconButton
                                    aria-label="Scroll featured tours left"
                                    onClick={() => scrollByOne(-1)}
                                    disabled={!canScrollPrev}
                                    size="small"
                                    sx={{
                                        display: { xs: 'none', sm: 'inline-flex' },
                                        position: 'absolute',
                                        left: { sm: -12, md: -20 },
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 2,
                                        ...navBtnSx,
                                        width: 44,
                                        height: 44,
                                    }}
                                >
                                    <ChevronLeftIcon sx={{ fontSize: 26 }} />
                                </IconButton>
                                <IconButton
                                    aria-label="Scroll featured tours right"
                                    onClick={() => scrollByOne(1)}
                                    disabled={!canScrollNext}
                                    size="small"
                                    sx={{
                                        display: { xs: 'none', sm: 'inline-flex' },
                                        position: 'absolute',
                                        right: { sm: -12, md: -20 },
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 2,
                                        ...navBtnSx,
                                        width: 44,
                                        height: 44,
                                    }}
                                >
                                    <ChevronRightIcon sx={{ fontSize: 26 }} />
                                </IconButton>
                            </>
                        )}

                        <Box
                            ref={scrollRef}
                            sx={{
                                display: 'flex',
                                alignItems: 'stretch',
                                gap: `${CARD_SCROLL_GAP_PX}px`,
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                scrollSnapType: 'x proximity',
                                scrollBehavior: 'smooth',
                                scrollbarWidth: 'none',
                                pb: 0,
                                px: { xs: 0.25, sm: 0 },
                                WebkitOverflowScrolling: 'touch',
                                '&::-webkit-scrollbar': { display: 'none' },
                            }}
                        >
                            {productCodes.map((code) => {
                                const product = productController.products.find((p) => p.productCode === code);
                                return (
                                    <HeroFeaturedProductCard
                                        key={`heroFeat-${code}`}
                                        product={product}
                                        isStoreLoading={productController.isLoading}
                                        isOnSale={isOnSale}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
}
